import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Plus, X, Trash2, Edit2, Calendar, FileText, Activity, User, Clock, Upload, ArrowLeft } from 'lucide-react';

const TIPOS_LICENCIA = ['Vacaciones', 'Enfermedad', 'Estudio', 'Especial', 'Otro'];
const ESTADOS = ['Activo', 'En licencia', 'Baja'];

import { fmtFecha } from '../utils/dates';


// Cuenta días corridos (incluye sábados y domingos) entre dos fechas, inclusive
function diasCorridos(from, to) {
  // Días que descuentan licencia: los DOMINGOS no cuentan (criterio contable:
  // el domingo no es día de licencia gozada)
  if (!from || !to) return 0;
  const a = new Date(from + 'T00:00:00');
  const b = new Date(to + 'T00:00:00');
  if (isNaN(a) || isNaN(b) || b < a) return 0;
  let count = 0;
  const d = new Date(a);
  while (d <= b) { if (d.getDay() !== 0) count++; d.setDate(d.getDate() + 1); }
  return count;
}

// ─── Métricas de actividad de un usuario desde el audit log ───
function calcActividad(logs, userName) {
  if (!userName) return null;
  const propios = logs.filter(l => (l.userName || '').toLowerCase() === userName.toLowerCase());
  const ahora = new Date();
  const haceDias = (n) => new Date(ahora.getTime() - n * 86400000);
  const semana = propios.filter(l => new Date(l.at) >= haceDias(7));
  const mes = propios.filter(l => new Date(l.at) >= haceDias(30));

  const porEntidad = {};
  mes.forEach(l => { porEntidad[l.entity] = (porEntidad[l.entity] || 0) + 1; });

  // últimos 7 días, conteo por día (para mini gráfico)
  const serie = [];
  for (let i = 6; i >= 0; i--) {
    const dia = haceDias(i);
    const ini = new Date(dia); ini.setHours(0,0,0,0);
    const fin = new Date(dia); fin.setHours(23,59,59,999);
    const n = propios.filter(l => { const t = new Date(l.at); return t >= ini && t <= fin; }).length;
    serie.push({ label: ['D','L','M','M','J','V','S'][dia.getDay()], n });
  }

  const ultima = propios.length ? propios.map(l => new Date(l.at)).sort((a,b)=>b-a)[0] : null;
  let ultimaTxt = 'Sin actividad registrada';
  if (ultima) {
    const dh = Math.floor((ahora - ultima) / 86400000);
    ultimaTxt = dh === 0 ? 'Hoy' : dh === 1 ? 'Ayer' : `Hace ${dh} días`;
  }

  return { semana: semana.length, mes: mes.length, porEntidad, serie, ultimaTxt, ultima, total: propios.length };
}

// ─── Badge de nivel de uso ───
function nivelUso(semana) {
  if (semana >= 15) return { txt: 'Uso alto', color: '#15803d', bg: '#dcfce7' };
  if (semana >= 5) return { txt: 'Uso medio', color: '#b45309', bg: '#fef3e2' };
  if (semana >= 1) return { txt: 'Uso bajo', color: '#b91c1c', bg: '#fee2e2' };
  return { txt: 'Sin uso', color: '#64748b', bg: '#f1f5f9' };
}

export default function EmployeesPage() {
  const { employees, addEmployee, updateEmployee, deleteEmployee, users, fetchAudit, currentUser, showToast } = useApp();
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetchAudit(500).then(d => setLogs(Array.isArray(d) ? d : [])).catch(() => setLogs([]));
  }, []);

  // Solo admin (chequeo después de los hooks para no romper las reglas de hooks)
  if (currentUser && currentUser.role !== 'admin') {
    return <div className="page"><div className="page-header"><h1 className="page-title">Empleados</h1></div>
      <p style={{ color: 'var(--text-muted)' }}>No tenés permisos para ver este módulo.</p></div>;
  }

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este empleado? Esta acción no se puede deshacer.')) return;
    try {
      const res = await deleteEmployee(id);
      if (res && res.needsConfirm) {
        // Tiene usuario de acceso vinculado: preguntamos qué hacer.
        const usr = res.linkedUser?.name || 'el usuario vinculado';
        const tambien = window.confirm(
          `Este empleado tiene un usuario de acceso vinculado (${usr}).\n\n` +
          `Aceptar = borrar también el usuario (pierde el acceso al sistema).\nCancelar = borrar solo el empleado (el usuario queda).`
        );
        await deleteEmployee(id, tambien);
      }
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      showToast && showToast('Error al eliminar: ' + e.message, 'error');
    }
  };

  // Si hay uno seleccionado, mostrar la ficha
  if (selected) {
    const emp = employees.find(e => e.id === selected.id) || selected;
    return <EmployeeDetail emp={emp} users={users} logs={logs}
      onBack={() => setSelected(null)}
      onEdit={() => { setEditEmp(emp); setModal(true); }}
      onSave={updateEmployee}
      modal={modal} setModal={setModal} editEmp={editEmp} setEditEmp={setEditEmp}
      addEmployee={addEmployee} updateEmployee={updateEmployee} />;
  }

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Empleados</h1>
          <p className="page-subtitle">{employees.length} {employees.length === 1 ? 'empleado' : 'empleados'} · escritorio de gestión interna</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditEmp(null); setModal(true); }}><Plus size={16} /> Nuevo empleado</button>
      </div>

      {employees.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <User size={40} style={{ opacity: 0.4, marginBottom: 12 }} />
          <p>Todavía no cargaste empleados.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModal(true)}>Crear el primero</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {employees.map(emp => {
            const user = users.find(u => u.id === emp.userId);
            const act = calcActividad(logs, user?.name || user?.email);
            const nivel = act ? nivelUso(act.semana) : nivelUso(0);
            const pendLic = (emp.licenses || []).filter(l => l.status === 'pendiente').length;
            return (
              <div key={emp.id} className="card" style={{ padding: 18, cursor: 'pointer' }} onClick={() => setSelected(emp)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{emp.name}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{emp.role || 'Sin cargo'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
                      background: emp.status === 'Activo' ? '#dcfce7' : emp.status === 'En licencia' ? '#fef3e2' : '#f1f5f9',
                      color: emp.status === 'Activo' ? '#15803d' : emp.status === 'En licencia' ? '#b45309' : '#64748b' }}>
                      {emp.status || 'Activo'}
                    </span>
                    <button className="btn btn-danger btn-sm" style={{ padding: '3px 7px' }} title="Eliminar empleado"
                      onClick={(e) => { e.stopPropagation(); handleDelete(emp.id); }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <div style={{ flex: 1, background: 'var(--bg-input)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{act ? act.semana : 0}</div>
                    <div style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>acciones / semana</div>
                  </div>
                  {pendLic > 0 && (
                  <div style={{ flex: 1, background: 'rgba(245,158,11,0.1)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--amber)' }}>{pendLic}</div>
                    <div style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>licencia{pendLic !== 1 ? 's' : ''} pendiente{pendLic !== 1 ? 's' : ''}</div>
                  </div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: nivel.bg, color: nivel.color }}>{nivel.txt}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{user ? `@ ${user.name || user.email}` : 'sin usuario'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <EmployeeModal emp={editEmp} users={users}
          onClose={() => { setModal(false); setEditEmp(null); }}
          onSaveNew={addEmployee} onSaveEdit={updateEmployee} />
      )}
    </div>
  );
}

// ─── Ficha de detalle ───
function EmployeeDetail({ emp, users, logs, onBack, onEdit, onSave, modal, setModal, editEmp, setEditEmp, addEmployee, updateEmployee }) {
  const user = users.find(u => u.id === emp.userId);
  const act = useMemo(() => calcActividad(logs, user?.name || user?.email), [logs, user]);
  const nivel = act ? nivelUso(act.semana) : nivelUso(0);
  // Solo las licencias APROBADAS descuentan saldo (las pendientes/rechazadas no)
  const tomados = (emp.licenses || []).filter(l => (l.status || 'aprobada') === 'aprobada').reduce((s, l) => s + (Number(l.days) || 0), 0);
  const total = Number(emp.licenseTotal) || 0;
  const saldo = total - tomados;
  const [tab, setTab] = useState('actividad'); // actividad | licencias | datos

  // Sub-formulario para agregar licencia
  const [licFrom, setLicFrom] = useState('');
  const [licTo, setLicTo] = useState('');
  const [licTipo, setLicTipo] = useState('Vacaciones');
  const [licNota, setLicNota] = useState('');
  const licDias = diasCorridos(licFrom, licTo);

  const addLicencia = () => {
    if (!licFrom || !licTo || licDias <= 0) { alert('Ingresá fechas válidas (desde y hasta).'); return; }
    const nueva = { id: Date.now().toString(), from: licFrom, to: licTo, days: licDias, type: licTipo, note: licNota, status: 'aprobada', decidedBy: 'carga directa' };
    onSave(emp.id, { licenses: [...(emp.licenses || []), nueva] });
    setLicFrom(''); setLicTo(''); setLicNota(''); setLicTipo('Vacaciones');
  };
  const delLicencia = (id) => {
    onSave(emp.id, { licenses: (emp.licenses || []).filter(l => l.id !== id) });
  };
  const aprobarLicencia = (id, status) => {
    onSave(emp.id, { licenses: (emp.licenses || []).map(l => l.id === id
      ? { ...l, status, decidedBy: 'admin', decidedAt: new Date().toISOString() } : l) });
  };

  // Documentos
  const fileRef = React.useRef();
  const subirDoc = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { alert('El archivo supera los 4MB.'); return; }
    const base64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    const doc = { id: Date.now().toString(), name: f.name, data: base64, uploadedAt: new Date().toISOString() };
    onSave(emp.id, { documents: [...(emp.documents || []), doc] });
    if (fileRef.current) fileRef.current.value = '';
  };
  const delDoc = (id) => {
    if (window.confirm('¿Eliminar este documento?')) onSave(emp.id, { documents: (emp.documents || []).filter(d => d.id !== id) });
  };
  const bajarDoc = (doc) => {
    const a = document.createElement('a');
    a.href = doc.data; a.download = doc.name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Notas
  const [notas, setNotas] = useState(emp.notes || '');
  const [notasDirty, setNotasDirty] = useState(false);
  useEffect(() => { setNotas(emp.notes || ''); setNotasDirty(false); }, [emp.id]);

  const maxSerie = act ? Math.max(1, ...act.serie.map(s => s.n)) : 1;

  return (
    <div className="page">
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 16 }}><ArrowLeft size={14} /> Volver</button>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">{emp.name}</h1>
          <p className="page-subtitle">{emp.role || 'Sin cargo'} · {user ? `usuario @${user.name || user.email}` : 'sin usuario asociado'}</p>
        </div>
        <button className="btn btn-secondary" onClick={onEdit}><Edit2 size={14} /> Editar datos</button>
      </div>

      {/* Pestañas: lo operativo (actividad) separado de lo de RRHH (licencias, datos) */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {[['actividad', 'Actividad'], ['licencias', 'Licencias'], ['datos', 'Datos']].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '8px 16px', fontSize: 13.5, fontWeight: tab === k ? 700 : 500,
            color: tab === k ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: tab === k ? '2px solid var(--accent)' : '2px solid transparent',
          }}>{lbl}</button>
        ))}
      </div>

      {tab !== 'actividad' && <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 }}>
        {tab === 'datos' && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 14 }}>Datos</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Cargo</span><strong>{emp.role || '—'}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Ingreso</span><strong>{emp.startDate ? fmtFecha(emp.startDate) : '—'}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Estado</span><strong>{emp.status || 'Activo'}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Usuario</span><strong>{user ? (user.name || user.email) : '—'}</strong></div>
          </div>
        </div>

        )}
        {tab === 'licencias' && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 14 }}><Calendar size={13} style={{ verticalAlign: 'middle' }} /> Licencia anual</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '10px 6px' }}>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{total || '—'}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>corresponden</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '10px 6px' }}>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{tomados}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>tomados</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', background: saldo < 0 ? '#fee2e2' : '#dcfce7', borderRadius: 8, padding: '10px 6px' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: saldo < 0 ? '#b91c1c' : '#15803d' }}>{total ? saldo : '—'}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>disponibles</div>
            </div>
          </div>
        </div>
        )}
      </div>}

      {/* Dashboard de actividad */}
      {tab === 'actividad' && (<>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', margin: 0 }}><Activity size={13} style={{ verticalAlign: 'middle' }} /> Uso del sistema</h3>
          {act && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: nivel.bg, color: nivel.color }}>{nivel.txt}</span>}
        </div>
        {!user ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Asociá un usuario del sistema para ver su actividad.</p>
        ) : !act || act.total === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin actividad registrada para este usuario.</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
              <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '12px 6px' }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>{act.semana}</div><div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>esta semana</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '12px 6px' }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>{act.mes}</div><div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>este mes</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '12px 6px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}><Clock size={12} style={{ verticalAlign: 'middle' }} /> {act.ultimaTxt}</div><div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>última actividad</div>
              </div>
            </div>

            {/* Mini gráfico 7 días */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>Actividad últimos 7 días</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 80 }}>
                {act.serie.map((s, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: s.n ? 'var(--text)' : 'var(--text-muted)' }}>{s.n || ''}</div>
                    <div style={{ width: '70%', height: `${(s.n / maxSerie) * 56}px`, minHeight: s.n ? 4 : 0,
                      background: s.n ? 'var(--accent)' : 'var(--border)', borderRadius: '4px 4px 0 0' }} />
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desglose por tipo */}
            {Object.keys(act.porEntidad).length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>Por tipo (último mes)</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(act.porEntidad).sort((a,b)=>b[1]-a[1]).map(([ent, n]) => (
                    <span key={ent} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 20, background: 'var(--bg-input)' }}>
                      {ent}: <strong>{n}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      </>)}

      {/* Historial de licencias */}
      {tab === 'licencias' && (
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 14 }}>Licencias y ausencias</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16, background: 'var(--bg-input)', padding: 14, borderRadius: 10 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Desde</label>
            <input type="date" value={licFrom} onChange={e => setLicFrom(e.target.value)} style={{ fontSize: 12 }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Hasta</label>
            <input type="date" value={licTo} onChange={e => setLicTo(e.target.value)} style={{ fontSize: 12 }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Tipo</label>
            <select value={licTipo} onChange={e => setLicTipo(e.target.value)} style={{ fontSize: 12 }}>
              {TIPOS_LICENCIA.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 120 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Nota (opcional)</label>
            <input value={licNota} onChange={e => setLicNota(e.target.value)} placeholder="" style={{ fontSize: 12 }} />
          </div>
          <div style={{ textAlign: 'center', minWidth: 50 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{licDias || '—'}</div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>días</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={addLicencia}><Plus size={13} /> Agregar</button>
        </div>
        {(emp.licenses || []).length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 12 }}>Sin licencias registradas.</p>
        ) : (
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px' }}>Desde</th><th style={{ padding: '8px 10px' }}>Hasta</th>
                <th style={{ padding: '8px 10px' }}>Días</th><th style={{ padding: '8px 10px' }}>Tipo</th>
                <th style={{ padding: '8px 10px' }}>Nota</th><th style={{ padding: '8px 10px' }}>Estado</th><th></th>
              </tr></thead>
              <tbody>
                {[...(emp.licenses || [])].sort((a,b) => new Date(b.from) - new Date(a.from)).map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px' }}>{fmtFecha(l.from)}</td>
                    <td style={{ padding: '8px 10px' }}>{fmtFecha(l.to)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700 }}>{l.days}</td>
                    <td style={{ padding: '8px 10px' }}>{l.type}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{l.note || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {(l.status || 'aprobada') === 'pendiente' ? (
                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <span className="badge badge-amber" style={{ fontSize: 9.5 }}>Pendiente</span>
                          <button className="btn btn-primary btn-sm" style={{ padding: '2px 7px', fontSize: 10.5 }} onClick={() => aprobarLicencia(l.id, 'aprobada')}>Aprobar</button>
                          <button className="btn btn-danger btn-sm" style={{ padding: '2px 7px', fontSize: 10.5 }} onClick={() => aprobarLicencia(l.id, 'rechazada')}>Rechazar</button>
                        </span>
                      ) : (l.status || 'aprobada') === 'rechazada' ? (
                        <span className="badge badge-red" style={{ fontSize: 9.5 }}>Rechazada</span>
                      ) : (
                        <span className="badge badge-green" style={{ fontSize: 9.5 }}>Aprobada</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      <button className="btn btn-danger btn-sm" style={{ padding: '3px 7px' }} onClick={() => delLicencia(l.id)}><Trash2 size={11} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      )}

      {/* Documentos */}
      {tab === 'datos' && (<>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', margin: 0 }}><FileText size={13} style={{ verticalAlign: 'middle' }} /> Documentos (contratos, etc.)</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}><Upload size={13} /> Subir</button>
          <input ref={fileRef} type="file" onClick={e => { e.target.value = null; }} onChange={subirDoc} style={{ display: 'none' }} />
        </div>
        {(emp.documents || []).length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 12 }}>Sin documentos. Hasta 4MB por archivo.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(emp.documents || []).map(doc => (
              <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => bajarDoc(doc)}>
                  <FileText size={15} style={{ color: 'var(--accent)' }} />
                  <span style={{ fontSize: 13 }}>{doc.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtFecha(doc.uploadedAt)}</span>
                </div>
                <button className="btn btn-danger btn-sm" style={{ padding: '3px 7px' }} onClick={() => delDoc(doc.id)}><Trash2 size={11} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notas */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 14 }}>Notas internas</h3>
        <textarea value={notas} onChange={e => { setNotas(e.target.value); setNotasDirty(true); }} rows={4}
          placeholder="Anotaciones internas sobre el empleado..." style={{ width: '100%', resize: 'vertical' }} />
        {notasDirty && (
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }}
            onClick={() => { onSave(emp.id, { notes: notas }); setNotasDirty(false); }}>Guardar notas</button>
        )}
      </div>
      </>)}
    </div>
  );
}

// ─── Modal de alta/edición ───
function EmployeeModal({ emp, users, onClose, onSaveNew, onSaveEdit }) {
  const isEdit = !!emp;
  const [form, setForm] = useState(emp || {
    name: '', role: '', startDate: '', status: 'Activo', userId: '', licenseTotal: 20, licenses: [], documents: [], notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { alert('El nombre es obligatorio.'); return; }
    setSaving(true);
    try {
      if (isEdit) await onSaveEdit(emp.id, form);
      else await onSaveNew(form);
      onClose();
    } catch (e) { alert('Error al guardar.'); setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>{isEdit ? 'Editar empleado' : 'Nuevo empleado'}</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Nombre *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nombre y apellido" />
            </div>
            <div className="form-group">
              <label className="form-label">Cargo</label>
              <input value={form.role} onChange={e => set('role', e.target.value)} placeholder="Ej: Administrativo" />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha de ingreso</label>
              <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Estado</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Días de licencia anual</label>
              <input type="number" value={form.licenseTotal} onChange={e => set('licenseTotal', e.target.value)} placeholder="20" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Usuario del sistema asociado</label>
              <select value={form.userId || ''} onChange={e => set('userId', e.target.value)}>
                <option value="">— Sin asociar —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
              </select>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Asociar el usuario permite ver la actividad del empleado en el sistema.</p>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary">{saving ? 'Guardando...' : (isEdit ? 'Guardar cambios' : 'Crear empleado')}</button>
        </div>
      </div>
    </div>
  );
}
